import React, { useEffect, useState } from 'react';
import { ButtonLarge } from '../../../common/sharedComponents/buttons/ButtonLarge';
import { BottomSheet } from '../../../common/sharedComponents/BottomSheet';
import { Card } from '../../../myAudit/components/Card';
import InfiniteScroll from 'react-infinite-scroll-component';
import { showErrorMessage } from '../../../../helpers/showMessage';
import {
  getMyAuditTemplates,
  getMyAuditTemplatesDeptList,
} from '../../../../requests/myAudit';
import { useNavigate } from 'react-router-dom';
import {
  DepartmentHeaderContainer,
  ControlSelectContainer,
  SearchFieldContainer,
  LineHeader,
  StyledMyAuditQuestionnaireContainer,
  LabelContainer,
  FilterHeaderContainer,
  ButtonsContainer,
  InformationFloatingContainer,
  InformationFloatingContent,
  CircleInfo,
} from './MyAuditChecklist.styled';
import { useTranslation } from 'react-i18next';
import { IDepartment, ITemplate } from '../../../@types/myAuditForm';
import { ClientTypes } from './../../../@types/card';
import {
  Body3,
  Body4,
  Body5,
  Headline3,
  Headline2,
} from '../../../common/typography';
import { FilterIcon } from '../../../common/icons/FilterIcon';
import { SearchField } from '../../../common/sharedComponents/SearchField';
import { IconButton } from '../../../common/sharedComponents/IconButton';
import { getItem, removeItem, setItem } from '../../../../utils/localStorage';
import { DropdownSingle } from './../../../common/sharedComponents/DropdownSingle';
import { IDropdownOption } from './../../../@types/dropdown';
import { SubHeaderSelector } from './../../../common/sharedComponents/SubHeaderSelector';
import { IListSelector } from './../../../@types/subHeaderSelector';
import {
  FilterContainer,
  FiltersContent,
  HeaderContainer,
} from '../../../common/sharedStyledComponents/MyAuditGenericStyles.styled';
import { AppHeader } from '../../../common/sharedComponents/AppHeader';
import { getBrandAndHotelData } from './../../../../helpers/handleBrandAndHotelData';
import {
  getInitialHotelSelectionData,
  updateHotelSelection,
} from './../../../../helpers/hotelSelectorHelpers';
import {
  NoDataMessageContainer,
  NoResultContainer,
} from '../MyAuditList/styled.myAuditList';
import { SearchFile } from '../../../common/icons/SearchFile';
import { QuestionnaireSkeleton } from './../../MyAuditSkeleton/QuestionnaireSkeleton';
import { ButtonBlockColumn } from './../../../common/sharedStyledComponents/GenericStyles.styled';
import { IMyAuditPayload } from '../../../@types/myAudit';
import { ChecklistTutorialModal } from '../../../myAudit/components/ChecklistTutorialModal';
import { RadioButton } from '../../../common/sharedComponents/RadioButton';

export function MyAuditChecklist () {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const selectedHotelId = getItem('selectedHotel');
  const selectedClientId = getItem('selectedClient');
  const selectedBrandId = getItem('selectedBrand');

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isFiltersOpen, setFiltersOpen] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [templates, setTemplates] = useState<ITemplate[]>([]);
  const [departmentList, setDepartmentList] = useState<IDropdownOption[]>([]);
  const [hotelList, setHotelList] = useState<IListSelector[]>([]);
  const [selectorSubTitle, setSelectorSubTitle] = useState<string>();
  const [selectorTitle, setSelectorTitle] = useState<string>();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isFilterApplied, setIsFilterApplied] = useState<boolean>(false);
  const [selectedDepartment, setSelectedDepartment] = useState<string>('');
  const [appliedFilterCount, setAppliedFilterCount] = useState<number>(0);
  const [clientType, setClientType] = useState<ClientTypes | undefined>(
    undefined
  );
  const [isModalInfo, setModalInfo] = useState<boolean>(false);

  const myAuditState: IMyAuditPayload = {
    hotelId: selectedHotelId,
    subject: '',
    templateId: 0,
    sections: [],
  };

  const handleTemplateSelection = (
    templateId: number,
    templateName: string
  ) => {
    myAuditState.templateId = templateId;
    setItem('myAudit', myAuditState);
    setItem('isFormData', true);
    navigate(`/myaudit/sections`, {
      state: {
        templateName,
      },
    });
  };

  const fetchTemplateData = (shouldFetchFilteredData = false) => {
    setIsFilterApplied(shouldFetchFilteredData);
    setIsLoading(true);
    getMyAuditTemplates({
      pageNumber,
      searchTerm,
      selectedDepartment,
      filterType: clientType,
    })
      .then((response) => {
        const templatesResponse: ITemplate[] = response.data.results;
        if (!templatesResponse) {
          setTemplates([]);
          setHasMore(false);
        }

        if (pageNumber === 1) {
          setTemplates(templatesResponse);
        } else {
          setTemplates([...templates, ...templatesResponse]);
        }

        if (templatesResponse.length < pageNumber) {
          setHasMore(false);
        } else {
          setPageNumber(pageNumber + 1);
        }

        updateFilterCounter();
      })
      .catch(() => {
        showErrorMessage(t('COMMON.ALERT_MESSAGES.UNABLE_TO_GET_LIST'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchDepartmentList = () => {
    getMyAuditTemplatesDeptList()
      .then((response) => {
        const departments: IDepartment[] = response.data;
        if (departments) {
          const dropdownDepartmentList: IDropdownOption[] = response.data.map(
            (item) => ({
              value: item.id,
              label: item.value,
            })
          );
          setDepartmentList(dropdownDepartmentList);
        }
      })
      .catch(() => {
        showErrorMessage(t('COMMON.ALERT_MESSAGES.UNABLE_TO_GET_LIST'));
      });
  };

  useEffect(() => {
    fetchTemplateData();
    fetchDepartmentList();
  }, [hotelList]);

  useEffect(() => {
    removeItem('myAudit');
    removeItem('editCard');
    const allHotels = getBrandAndHotelData();
    const { hotelName, brandName, hotelsWithSelection } =
      getInitialHotelSelectionData(allHotels);

    setHotelList(hotelsWithSelection);
    setSelectorTitle(brandName);
    setSelectorSubTitle(hotelName);
  }, []);

  function handleSelectHotel (
    hotelId: string | number,
    clientId: string | number,
    brandId: string | number
  ) {
    const { updatedHotelList, selectedHotel } = updateHotelSelection(
      hotelList,
      hotelId,
      clientId,
      brandId
    );

    if (selectedHotel) {
      setTemplates([]);
      setPageNumber(1);
      setSelectorTitle(selectedHotel.brandName);
      setSelectorSubTitle(selectedHotel.name);
    }

    setHotelList(updatedHotelList);
  }

  function clearFilters () {
    const filters = {
      pageNumber: 1,
      searchTerm: '',
      selectedDepartment: '',
      filterType: undefined,
    };
    setSelectedDepartment('');
    setSearchTerm('');
    setClientType(undefined);
    setIsLoading(true);
    getMyAuditTemplates(filters)
      .then((response) => {
        const templatesResponse: ITemplate[] = response.data.results;
        if (templatesResponse) {
          setTemplates(templatesResponse);
        }
        setAppliedFilterCount(0);
      })
      .catch(() => {
        showErrorMessage(t('COMMON.ALERT_MESSAGES.UNABLE_TO_GET_LIST'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  function updateFilterCounter () {
    let count = 0;
    if (searchTerm) { count++; }
    if (selectedDepartment) { count++; }
    if (clientType) { count++; }
    setAppliedFilterCount(count);
  }

  function handleModalInfoQuestionnaire () {
    setModalInfo(true);
  }

  return (
    <>
      <InformationFloatingContainer>
        <InformationFloatingContent onClick={handleModalInfoQuestionnaire}>
          <Body5 color="white">
            {t('COMMON.MY_AUDIT.PAGES.CHECKLIST.CREATE_NEW_CHECKLIST')}
          </Body5>
        </InformationFloatingContent>
        <CircleInfo onClick={handleModalInfoQuestionnaire}>
          <Headline2 color="white">i</Headline2>
        </CircleInfo>
      </InformationFloatingContainer>
      <HeaderContainer>
        <AppHeader
          title={t('COMMON.MY_AUDIT.TITLE')}
          navigateTo={'/myactions'}
        />
        <LineHeader />
        <SubHeaderSelector
          title={selectorTitle}
          subTitle={selectorSubTitle}
          listSelector={hotelList}
          clickSelectSection={handleSelectHotel}
        />
      </HeaderContainer>
      <StyledMyAuditQuestionnaireContainer>
        <DepartmentHeaderContainer>
          <Headline3>
            {t('COMMON.MY_AUDIT.PAGES.CHECKLIST.SELECT_CHECKLIST')}
          </Headline3>
          <FilterContainer>
            <IconButton
              title={`${t('COMMON.MY_AUDIT.BUTTON.FILTER')} ${appliedFilterCount > 0 ? ` (${appliedFilterCount})` : ''}`}
              rightIcon={<FilterIcon />}
              onClick={() => {
                setFiltersOpen(true);
                setPageNumber(1);
              }}
            />
          </FilterContainer>
        </DepartmentHeaderContainer>
        {isLoading && <QuestionnaireSkeleton />}
        {templates.length > 0 && (
          <InfiniteScroll
            dataLength={templates.length} // This is important to prevent unnecessary re-renders
            next={() => fetchTemplateData(isFilterApplied)}
            hasMore={hasMore}
            loader={isLoading && <QuestionnaireSkeleton />}
          >
            {templates.map((template: ITemplate) => {
              return (
                <Card
                  key={template.id}
                  title={template.templateName}
                  client={template.templateAccess.hotelId ? 'hotel' : 'brand'}
                  description={template.description}
                  department={
                    template.templateAccess.category &&
                    template.templateAccess.category.value
                  }
                  onClick={() =>
                    handleTemplateSelection(template.id, template.templateName)
                  }
                />
              );
            })}
          </InfiniteScroll>
        )}
        {!isLoading && templates.length === 0 && (
          <NoResultContainer>
            <SearchFile />
            <Body3 color="eerieBlack">
              {t('COMMON.MY_AUDIT.QUESTIONNAIRE.NO_DATA.TITLE')}
            </Body3>
            <NoDataMessageContainer>
              <Body4 color="eerieBlack">
                {t('COMMON.MY_AUDIT.QUESTIONNAIRE.NO_DATA.MESSAGE')}
              </Body4>
            </NoDataMessageContainer>
          </NoResultContainer>
        )}
      </StyledMyAuditQuestionnaireContainer>
      <BottomSheet
        isOpen={isFiltersOpen}
        onClose={() => {
          setFiltersOpen(false);
        }}
        title={t('COMMON.MY_AUDIT.BUTTON.FILTER')}
      >
        <FiltersContent>
          <FilterHeaderContainer>
            <RadioButton
              label={t('COMMON.MY_AUDIT.LABELS.BRAND')}
              checked={clientType === 'brand'}
              onClick={() => {
                setClientType('brand');
              }}
            />
            <RadioButton
              checked={clientType === 'hotel'}
              label={t('COMMON.MY_AUDIT.LABELS.HOTEL')}
              onClick={() => {
                setClientType('hotel');
              }}
            />
            <RadioButton
              checked={!clientType}
              label={t('COMMON.MY_AUDIT.LABELS.ALL')}
              onClick={() => {
                setClientType(undefined);
              }}
            />
          </FilterHeaderContainer>
          <SearchFieldContainer>
            <SearchField
              placeholderText={t(
                'COMMON.MY_AUDIT.GENERIC_FIELDS.TYPING_SOMETHING_HERE'
              )}
              textValue={searchTerm}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setSearchTerm(event.target.value);
              }}
              onClearTextValue={() => {
                setSearchTerm('');
              }}
              label={t('COMMON.MY_AUDIT.TITLES.MY_AUDIT_NAME_NOTES')}
            />
          </SearchFieldContainer>
          <ControlSelectContainer>
            <LabelContainer>
              <Body5 color="eerieBlack">
                {t('COMMON.MY_AUDIT.TITLES.DEPARTMENT')}
              </Body5>
            </LabelContainer>
            {selectedDepartment !== '' ? (
              <DropdownSingle
                options={departmentList}
                menuPlacement="top"
                callback={(event) => {
                  setSelectedDepartment(event.value);
                }}
              />
            ) : (
              <DropdownSingle
                defaultOption={null}
                options={departmentList}
                menuPlacement="top"
                callback={(event) => {
                  setSelectedDepartment(event.value);
                }}
              />
            )}
          </ControlSelectContainer>
          <ButtonsContainer>
            <ButtonBlockColumn>
              <ButtonLarge
                title={t('COMMON.MY_AUDIT.BUTTON.SHOW_RESULT')}
                onClick={() => {
                  setPageNumber(1);
                  setTemplates([]);
                  setFiltersOpen(false);
                  fetchTemplateData(true);
                }}
              />
              <ButtonLarge
                outlined={true}
                inverted={true}
                backgroundColor="eerieBlack"
                title={t('COMMON.MY_AUDIT.BUTTON.CLEAR_FILTERS')}
                onClick={() => {
                  setFiltersOpen(false);
                  clearFilters();
                }}
              />
            </ButtonBlockColumn>
          </ButtonsContainer>
        </FiltersContent>
      </BottomSheet>
      {isModalInfo && (
        <ChecklistTutorialModal
          handleModalClose={() => setModalInfo(false)}
          isFullModal={true}
          isOpen={isModalInfo}
          isShowCloseIcon={true}
        />
      )}
    </>
  );
}
